/**
 * Unified analytics module for Radix Exchange.
 *
 * Abstracts GA4 and Mixpanel behind a single interface so every call
 * fans out to both providers. Environment-based toggling ensures that
 * analytics are silent in local / development unless explicitly opted-in
 * via REACT_APP_ANALYTICS_DEBUG=true.
 *
 * Usage:
 *   import { initAnalytics, trackEvent } from '../analytics';
 *   initAnalytics();
 *   trackEvent(AnalyticsEvents.PAGE_VIEW, { path: '/trade' });
 */

import { initGA4, trackGA4Event } from './providers/ga4';
import { initMixpanel, setMixpanelSuperProperties, trackMixpanelEvent } from './providers/mixpanel';
import { captureUtmParams, getStoredUtmParams, type UtmParams } from './utm';

export { AnalyticsEvents, type AnalyticsEventName } from './events';
export { identifyMixpanelUser, resetMixpanel } from './providers/mixpanel';
export { captureUtmParams, getStoredUtmParams, clearUtmParams } from './utm';

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'local';
const ANALYTICS_DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === 'true';

/**
 * Returns true when analytics providers should be active.
 * In local/dev environments, providers are only active when
 * REACT_APP_ANALYTICS_DEBUG=true.
 */
function isEnabled(): boolean {
  if (ENVIRONMENT === 'local' || ENVIRONMENT === 'development' || ENVIRONMENT === 'dev') {
    return ANALYTICS_DEBUG;
  }
  return true;
}

let _initialised = false;

/**
 * Initialise all analytics providers and capture UTM parameters.
 * Safe to call multiple times; subsequent calls are no-ops.
 */
export function initAnalytics(): void {
  if (_initialised) return;

  // Always capture UTM params (useful even if providers are off)
  const utm = captureUtmParams();

  if (!isEnabled()) {
    _initialised = true;
    return;
  }

  initGA4();
  initMixpanel();

  // Register UTM params as Mixpanel super-properties
  const storedUtm = Object.keys(utm).length > 0 ? utm : getStoredUtmParams();
  if (Object.keys(storedUtm).length > 0) {
    setMixpanelSuperProperties(storedUtm);
  }

  _initialised = true;
}

/**
 * Send an event to all active analytics providers.
 * UTM parameters stored in sessionStorage are automatically
 * merged into the event properties sent to GA4.
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (!isEnabled()) return;

  const utm: UtmParams = getStoredUtmParams();
  const enrichedProps = { ...utm, ...properties };

  trackGA4Event(eventName, enrichedProps);
  trackMixpanelEvent(eventName, enrichedProps);
}

/**
 * Convenience helper for page-view tracking.
 */
export function trackPageView(path: string, title?: string): void {
  const pageTitle = title || document.title;
  trackEvent('page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: pageTitle,
    page_referrer: document.referrer,
  });
}
